/**
 * The wiring behind the Ankiety tab: three reads, the facets, and which form is on the right.
 *
 * Behaviour in `model/`, wiring in a hook, rendering in the components — so everything below is
 * about WHEN things are read and what they are called, and nothing below decides anything a test
 * would want to reach.
 *
 * ── The LIST waits for all three of its reads ────────────────────────────────────────────────
 * The templates, the intake and the roster. A column drawn from the first of them is a partial
 * answer wearing the shape of a complete one: the cards would stand with no faces and then grow
 * them, and the „Obowiązkowa" group would appear a beat after the trainer's own. One gate over the
 * union, which is what `useSkeletonGate` asks for.
 *
 * The right-hand card has a gate of its own, because it is a different box with a different reserve
 * and its read changes every time the selection does.
 *
 * ── The EXPOSED state is the wait branch, tail and all ───────────────────────────────────────
 * `listState` and `detailState` say „pending" for as long as the gate says the wait belongs on
 * screen, which outlasts the read itself by the skeleton's 300ms minimum life. A screen forking on
 * them is therefore forking on the gate, and the content that replaces the bars carries the reveal
 * in its first render rather than appearing at full opacity and fading again a beat later.
 *
 * The two verdicts the READS give are kept below as `listAnswer` and `detailAnswer` and are not
 * exposed. The card inherits the list's RAW verdict, never the gated one: feeding a gated state
 * into the card's own gate would stack a second 300ms tail on top of the list's. Inheriting the raw
 * one keeps the two gates in lockstep instead, because they open and close on the same render.
 *
 * ── The FACETS are the screen's, the selection is this hook's ────────────────────────────────
 * The search field and the filter button stand in the tab strip's own row — which is the screen's
 * chrome, not this tab's panel — so the value they narrow by is owned one level up and arrives here
 * as a parameter. The alternative was a second slot on `TabStrip` or a context for one consumer;
 * a parameter is the smaller interface and it is the one that makes `filterSurveyGroups` reachable
 * from a test without a renderer.
 *
 * ── The selection is state, not an address ───────────────────────────────────────────────────
 * `?tab=` says which of the three tabs is open and nothing else. Which card is being previewed is
 * where the pointer is, not where the trainer is — the way INTO a form is `/raporty/$id`, which is
 * an address, and putting the preview in the URL too would give one form two of them. The plan
 * library's inspector stands on the same rule.
 *
 * It falls back rather than being corrected: if the selected form leaves the list — a filter hides
 * it, a delete removes it — the first row of what is left is previewed. Correcting state during a
 * render is what a derived value is for.
 *
 * ── The card's switch WRITES, and it is the editor's own write ───────────────────────────────
 * KANON `01-ankiety.html` draws a `.tog` on every check-in card, and pausing a form from the list
 * is the one act the column offers beside choosing one. It is `PATCH /survey/template/{id}` with
 * `{ isActive }` — the same one `useSurveyEditor.togglePaused` sends, not a second endpoint and not
 * a second shape — followed by an invalidation of the screen's own prefix.
 *
 * **Not optimistic.** A form the trainer believes is paused while the write is still open is the
 * worst possible half-truth on this screen: it decides whether their clients are asked anything
 * this week. The switch stays where it was until the server answers, `pausing` says which row is
 * mid-write, and a failure leaves the truth on screen rather than rolling a lie back.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { usePatchSurveyTemplateId } from '~/shared/api/generated/endpoints/survey/survey';

import {
  reportsRoot,
  clientFacesQueryOptions,
  intakeTemplateQueryOptions,
  surveyTemplateQueryOptions,
  surveyTemplatesQueryOptions,
} from '../api/reportsQueries';
import { clientNames, type ClientFace } from '../model/clientFaces';
import {
  filterSurveyGroups,
  surveyListIsNarrowed,
  type SurveyFacets,
} from '../model/surveyFilters';
import { surveyGroups, surveyRowsInOrder, type SurveyGroups, type SurveyRow } from '../model/surveyList';
import { surveySections, type DetailSection } from '~/features/survey-question-editor';

/** What the left column is showing. Four answers, and `rows` is reached from exactly one of them. */
export type SurveyListState = 'pending' | 'error' | 'empty' | 'noResults' | 'rows';

/** What the right-hand card is showing. */
export type SurveyDetailState = 'pending' | 'error' | 'ready' | 'none';

export interface SurveysTabView {
  /** What the column draws. „pending" covers the whole gated wait, not only the open read. */
  readonly listState: SurveyListState;
  /** Whether the bars belong inside that wait — false through the gate's 150ms of grace. */
  readonly listSkeleton: boolean;
  readonly listReveal: string;
  /** Narrowed, and in the two groups the column draws. */
  readonly groups: SurveyGroups;
  readonly faces: ReadonlyMap<number, ClientFace>;
  readonly selected: SurveyRow | null;
  readonly onSelect: (id: number) => void;
  readonly detailState: SurveyDetailState;
  readonly detailSkeleton: boolean;
  readonly detailReveal: string;
  readonly sections: readonly DetailSection[];
  /** The template id currently mid-write, or `null`. Its switch is refused while it is open. */
  readonly pausingId: number | null;
  readonly onSetActive: (id: number, active: boolean) => void;
  readonly onRetry: () => void;
}

export function useSurveysTab(facets: SurveyFacets): SurveysTabView {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const queryClient = useQueryClient();
  const patch = usePatchSurveyTemplateId();
  const [pausingId, setPausingId] = useState<number | null>(null);

  const templates = useQuery(surveyTemplatesQueryOptions());
  const intake = useQuery(intakeTemplateQueryOptions());
  const roster = useQuery(clientFacesQueryOptions());

  const [picked, setPicked] = useState<number | null>(null);

  const faces: ReadonlyMap<number, ClientFace> = roster.data ?? new Map();
  const groups = filterSurveyGroups(surveyGroups(templates.data, locale), facets, clientNames(faces));
  const visible = surveyRowsInOrder(groups);

  // The fallback, not a correction: the picked form may have been filtered out from under the
  // trainer, and the first row of what is left is what the card then previews.
  const selected = visible.find((row) => row.id === picked) ?? visible[0] ?? null;

  const listPending = templates.isPending || intake.isPending || roster.isPending;
  const listFailed = templates.isError || intake.isError || roster.isError;
  const listAnswer: SurveyListState = listPending
    ? 'pending'
    : listFailed
      ? 'error'
      : visible.length > 0
        ? 'rows'
        : surveyListIsNarrowed(facets)
          ? 'noResults'
          : 'empty';
  const listGate = useSkeletonGate(listAnswer === 'pending');
  const listState: SurveyListState = listGate.waiting ? 'pending' : listAnswer;

  // The intake's questions came with the list's own intake read; a check-in's need a read of their
  // own. Two sources, one card — which is why the state below is computed from whichever is live.
  const showingIntake = selected !== null && selected.mandatory;
  const detail = useQuery(surveyTemplateQueryOptions(showingIntake || selected === null ? null : selected.id));

  const detailQuery = showingIntake ? intake : detail;
  // A card with nothing in it because the LIST has not answered is not the same fact as a card with
  // nothing in it because the trainer has no forms. „Wybierz ankietę z listy" beside a column of
  // skeletons is an instruction the trainer cannot follow — measured in the running app with 900ms
  // of injected latency — so the card follows the list through the wait and only says „none" once
  // the list has answered and there is still nothing to point at.
  const detailAnswer: SurveyDetailState =
    selected === null
      ? listAnswer === 'pending'
        ? 'pending'
        : listAnswer === 'error'
          ? 'error'
          : 'none'
      : detailQuery.isPending
        ? 'pending'
        : detailQuery.isError
          ? 'error'
          : 'ready';
  const detailGate = useSkeletonGate(detailAnswer === 'pending');
  const detailState: SurveyDetailState = detailGate.waiting ? 'pending' : detailAnswer;

  return {
    listState,
    listSkeleton: listGate.skeleton,
    listReveal: listGate.reveal,
    groups,
    faces,
    selected,
    onSelect: setPicked,
    detailState,
    detailSkeleton: detailGate.skeleton,
    detailReveal: detailGate.reveal,
    // Off the RAW verdict: the sections are read by the card only once it is out of its wait, and
    // computing them a render early costs nothing while keeping this free of the tail.
    sections: detailAnswer === 'ready' ? surveySections(detailQuery.data, t) : [],
    pausingId,
    onSetActive: (id, active) => {
      if (pausingId !== null) return;
      setPausingId(id);
      patch.mutate(
        { id, data: { isActive: active } },
        {
          onSettled: () => {
            setPausingId(null);
            void queryClient.invalidateQueries({ queryKey: reportsRoot });
          },
        },
      );
    },
    onRetry: () => {
      void templates.refetch();
      void intake.refetch();
      void roster.refetch();
    },
  };
}
